import './FavoriteButton.scss'

import React, { useState, useEffect, memo } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { gql } from '@apollo/client'

import useUserContext from '../hooks/useUserContext'
import { getRequestHeader } from '../utils/api'
import { useFavoriteProgramLazyQuery, useAddFavoriteMutation, useRemoveFavoriteMutation } from '../types/generated'

import Loading from '../components/Loading'

interface Props {
  programId: number
  programSlug: string
  onShowingModal: () => void
}

export default memo(function FavoriteButton({ programId, programSlug, onShowingModal }: Props) {
  const { t } = useTranslation()
  const [isFavorite, setIsFavorite] = useState(false)
  const { isAuthenticated } = useUserContext()
  const [getFavoriteProgram, { data, loading, error, called }] = useFavoriteProgramLazyQuery({
    context: {
      headers: getRequestHeader({ isAuthRequest: true }),
    },
    variables: {
      programSlug,
    },
    fetchPolicy: 'network-only',
  })
  const [addFavorite, { loading: addFavoriteLoading }] = useAddFavoriteMutation({
    context: {
      headers: getRequestHeader({ isAuthRequest: true }),
    },
    variables: {
      input: {
        programSlug,
      },
    },
    update: (cache, { data }) => {
      cache.modify({
        fields: {
          favorites(existingFavoriteRef = { nodes: [] }) {
            const newFavoriteRef = cache.writeFragment({
              data: data?.addFavorite.item,
              fragment: gql`
                fragment NewFavorite on Program {
                  id
                }
              `,
            })
            return {
              ...existingFavoriteRef,
              nodes: [newFavoriteRef].concat(existingFavoriteRef.nodes),
            }
          },
        },
      })
    },
    onCompleted: () => {
      setIsFavorite(true)
    },
    onError: e => {
      // FIXME: Change to appropriate showing logic e.g. notify through modal
      console.log(e)
    },
  })
  const [removeFavorite, { loading: deleteFavoriteLoading }] = useRemoveFavoriteMutation({
    context: {
      headers: getRequestHeader({ isAuthRequest: true }),
    },
    variables: {
      input: {
        programSlug,
      },
    },
    update: cache => {
      cache.modify({
        fields: {
          favorites(existingFavoriteRef, { readField }) {
            return {
              ...existingFavoriteRef,
              nodes: existingFavoriteRef.nodes.filter((node: any) => programId !== readField('id', node)),
            }
          },
        },
      })
    },
    onCompleted: () => {
      setIsFavorite(false)
    },
    onError: e => {
      // FIXME: Change to appropriate showing logic e.g. notify through modal
      console.log(e)
    },
  })

  const handleClick = () => {
    if (!isAuthenticated) {
      onShowingModal()
      return
    }

    if (isFavorite) {
      removeFavorite()
      return
    }

    addFavorite()
  }

  useEffect(() => {
    if (!isAuthenticated) {
      setIsFavorite(false)
      return
    }
    getFavoriteProgram()
    // eslint-disable-next-line
  }, [programSlug, isAuthenticated])

  useEffect(() => {
    if (!called) {
      return
    }

    if (error) {
      setIsFavorite(false)
      return
    }
    // NOTE: If user had not favorite this program, api return 404 error.
    setIsFavorite(!!data?.favoriteProgram.result)
    // eslint-disable-next-line
  }, [called, error, data])

  const isLoading = addFavoriteLoading || deleteFavoriteLoading || loading
  const buttonTitleKey = isFavorite && isAuthenticated ? 'program.favoriteAdded' : 'program.favorite'

  return (
    <button
      id="FavoriteToggle"
      className={classNames('FavoriteButton', {
        'FavoriteButton--active': isAuthenticated && isFavorite,
      })}
      disabled={isLoading}
      onClick={handleClick}
    >
      {isLoading ? <Loading visible /> : t(buttonTitleKey)}
    </button>
  )
})
